import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn.js';

function Overlay({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[90]">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 h-full w-full cursor-default bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {children}
    </div>,
    document.body,
  );
}

/** Centrēts modālis (apstiprinājumi, mazas formas). */
export function Modal({
  open,
  onClose,
  title,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  if (!open) return null;
  return (
    <Overlay onClose={onClose}>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
        {/* biome-ignore lint/a11y/useSemanticElements: kontrolēts portāla modālis */}
        <div
          role="dialog"
          aria-modal="true"
          className={cn(
            'pointer-events-auto w-full max-w-lg animate-[dialog-in_.15s_ease-out] rounded-xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-800 dark:bg-slate-900',
            className,
          )}
        >
          {title ? (
            <h2 className="mb-3 text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
          ) : null}
          {children}
        </div>
      </div>
    </Overlay>
  );
}

/** Sānu panelis (rindas darbības, detaļu formas) — slīd no labās puses. */
export function SlideOver({
  open,
  onClose,
  title,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  if (!open) return null;
  return (
    <Overlay onClose={onClose}>
      {/* biome-ignore lint/a11y/useSemanticElements: kontrolēts portāla sānu panelis */}
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'absolute inset-y-0 right-0 flex w-full max-w-md animate-[slideover-in_.2s_ease-out] flex-col overflow-y-auto border-l border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-800 dark:bg-slate-900',
          className,
        )}
      >
        {title ? (
          <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
        ) : null}
        {children}
      </div>
    </Overlay>
  );
}
